/**
 * useFetch Hook
 * Reusable data fetching hook with loading, error, and refetch capabilities
 *
 * Usage:
 * const { data, isLoading, error, refetch } = useFetch(
 *     () => contactService.getContacts(),
 *     { immediate: true }
 * );
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { handleApiError } from '@/utils/errorHandler';

interface UseFetchOptions<T> {
    /** Execute fetch immediately on mount */
    immediate?: boolean;
    /** Dependencies that trigger refetch */
    deps?: unknown[];
    /** Initial data value */
    initialData?: T;
    /** Action name for error messages */
    actionName?: string;
    /** Callback on success */
    onSuccess?: (data: T) => void;
    /** Callback on error */
    onError?: (error: unknown) => void;
}

interface UseFetchReturn<T> {
    data: T | undefined;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    setData: React.Dispatch<React.SetStateAction<T | undefined>>;
    reset: () => void;
}

export function useFetch<T>(
    fetcher: () => Promise<T>,
    options: UseFetchOptions<T> = {}
): UseFetchReturn<T> {
    const {
        immediate = true,
        deps = [],
        initialData,
        actionName = 'fetch data',
        onSuccess,
        onError,
    } = options;

    const [data, setData] = useState<T | undefined>(initialData);
    const [isLoading, setIsLoading] = useState(immediate);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useRef(true);
    const fetcherRef = useRef(fetcher);

    // Keep fetcher ref updated
    fetcherRef.current = fetcher;

    const refetch = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await fetcherRef.current();

            if (mountedRef.current) {
                setData(result);
                onSuccess?.(result);
            }
        } catch (err) {
            if (mountedRef.current) {
                const errorMessage = handleApiError(err, actionName, { showToast: true });
                setError(errorMessage);
                onError?.(err);
            }
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [actionName, onSuccess, onError]);

    const reset = useCallback(() => {
        setData(initialData);
        setError(null);
        setIsLoading(false);
    }, [initialData]);

    useEffect(() => {
        mountedRef.current = true;

        if (immediate) {
            refetch();
        }

        return () => {
            mountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return {
        data,
        isLoading,
        error,
        refetch,
        setData,
        reset,
    };
}

/**
 * useFetchWithResponse Hook
 * Similar to useFetch but expects ApiResponse structure
 */
interface ApiResponse<T> {
    data: T;
    success: boolean;
    message?: string;
}

export function useFetchWithResponse<T>(
    fetcher: () => Promise<ApiResponse<T>>,
    options: UseFetchOptions<T> = {}
): UseFetchReturn<T> {
    const wrappedFetcher = useCallback(async () => {
        const response = await fetcher();
        if (response.success) {
            return response.data;
        }
        throw new Error(response.message || 'Request failed');
    }, [fetcher]);

    return useFetch(wrappedFetcher, options);
}

export default useFetch;
